/**
 * API calls for the packaging-report rating page.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'` — there is
 * no bearer token anywhere in this codebase. Unlike /rlhf/*, this surface is open to any logged-in
 * user, so there is no rater-access failure mode to translate.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export interface PackagingReportSummary {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  /** This user's OWN score. Null when they haven't rated it — never another user's. */
  my_rating: number | null;
  my_remarks: string | null;
  rated_at: string | null;
}

export interface PackagingReportDetail {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  /** Self-contained HTML (data: images and fonts) — rendered in the sandboxed iframe. */
  html: string;
}

export interface SavedRating {
  id: number;
  report_id: number;
  rating: number;
  remarks: string | null;
  updated_at: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    // Surface the backend's own message where it has one — it explains exactly why a save was
    // rejected (score out of range, remarks too long, report retired).
    let detail = '';
    try {
      detail = (await response.json())?.detail ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return response.json();
}

/** Every active report, each carrying this user's own rating so the page can rehydrate. */
export const fetchReports = () =>
  request<{ reports: PackagingReportSummary[] }>('/packaging-rating/reports')
    .then((r) => r.reports);

/** One report's HTML body — fetched separately from the list because it is ~1 MB. */
export const fetchReport = (reportId: number) =>
  request<PackagingReportDetail>(`/packaging-rating/reports/${reportId}`);

/** Save or revise this user's score. Idempotent — re-saving edits rather than duplicating. */
export const saveRating = (reportId: number, body: { rating: number; remarks: string }) =>
  request<SavedRating>(`/packaging-rating/reports/${reportId}/rating`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
